<script>
import { reactive, onMounted } from 'vue'
import mensagensRepository from '@/services/api/mensagensRepository'
import Editor from '@tinymce/tinymce-vue'

export default {
  name: 'MensagensAutomaticas',
  components: {
    Editor
  },
  setup() {
    const state = reactive({
      mensagens: [],
      mensagemAtual: {
        message_type: 'aniversario',
        message_title: '',
        message_text: '',
        arquivo_path: null,
        incluir_assinatura_treinador: true,
        ativo: true
      },
      tipoSelecionado: 'aniversario',
      loading: false,
      uploading: false,
      error: null,
      success: null,
      previewImagem: null
    })

    const tiposMensagem = [
      { value: 'aniversario', label: '🎂 Aniversário', descricao: 'Enviado no dia do aniversário do aluno' },
      { value: 'natal', label: '🎄 Natal', descricao: 'Enviado em 25 de dezembro' },
      { value: 'anonovo', label: '🎆 Ano Novo', descricao: 'Enviado em 31 de dezembro' }
    ]

    const buscarMensagens = async () => {
      try {
        state.loading = true
        const response = await mensagensRepository.MensagensGet()

        if (response.data.success) {
          state.mensagens = response.data.data || []
          carregarMensagemPorTipo(state.tipoSelecionado)
        }
      } catch (error) {
        console.error('Erro ao buscar mensagens:', error)
        state.error = 'Erro ao carregar mensagens'
      } finally {
        state.loading = false
      }
    }

    const carregarMensagemPorTipo = (tipo) => {
      const mensagem = state.mensagens.find(m => m.message_type === tipo)

      if (mensagem) {
        state.mensagemAtual = { ...mensagem }
        state.previewImagem = mensagem.arquivo_path
          ? `${process.env.VUE_APP_URL || window.location.origin}/storage/${mensagem.arquivo_path}`
          : null
      } else {
        resetarFormulario(tipo)
      }
    }

    const resetarFormulario = (tipo) => {
      state.mensagemAtual = {
        message_type: tipo,
        message_title: getTituloDefault(tipo),
        message_text: '',
        arquivo_path: null,
        incluir_assinatura_treinador: true,
        ativo: true
      }
      state.previewImagem = null
    }

    const getTituloDefault = (tipo) => {
      const titulos = {
        'aniversario': 'Feliz Aniversário!',
        'natal': 'Feliz Natal!',
        'anonovo': 'Feliz Ano Novo!'
      }
      return titulos[tipo] || ''
    }

    const selecionarTipo = (tipo) => {
      state.tipoSelecionado = tipo
      carregarMensagemPorTipo(tipo)
      state.error = null
      state.success = null
    }

    const handleFileUpload = async (event) => {
      const file = event.target.files[0]

      if (!file) return

      // Validar tipo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        state.error = 'Tipo de arquivo não permitido. Use: JPG, PNG, GIF ou WEBP'
        return
      }

      // Validar tamanho (5MB)
      if (file.size > 5242880) {
        state.error = 'Arquivo muito grande. Máximo: 5MB'
        return
      }

      try {
        state.uploading = true
        state.error = null

        const formData = new FormData()
        formData.append('file', file)

        const response = await mensagensRepository.MensagensUpload(formData)

        if (response.data.success) {
          state.mensagemAtual.arquivo_path = response.data.data.path
          state.previewImagem = URL.createObjectURL(file)
          state.success = 'Imagem enviada com sucesso!'
        }
      } catch (error) {
        console.error('Erro ao fazer upload:', error)
        state.error = 'Erro ao enviar imagem'
      } finally {
        state.uploading = false
      }
    }

    const removerImagem = () => {
      state.mensagemAtual.arquivo_path = null
      state.previewImagem = null
    }

    const salvar = async () => {
      try {
        state.loading = true
        state.error = null
        state.success = null

        // Validações
        if (!state.mensagemAtual.message_title || !state.mensagemAtual.message_text) {
          state.error = 'Preencha o título e a mensagem'
          return
        }

        let response

        if (state.mensagemAtual.id) {
          // Atualizar
          response = await mensagensRepository.MensagensPut(
            state.mensagemAtual.id,
            state.mensagemAtual
          )
        } else {
          // Criar
          response = await mensagensRepository.MensagensPost(state.mensagemAtual)
        }

        if (response.data.success) {
          state.success = 'Mensagem salva com sucesso!'
          await buscarMensagens()
        }
      } catch (error) {
        console.error('Erro ao salvar:', error)
        state.error = error.response?.data?.message || 'Erro ao salvar mensagem'
      } finally {
        state.loading = false
      }
    }

    const deletar = async () => {
      if (!state.mensagemAtual.id) {
        state.error = 'Nenhuma mensagem para deletar'
        return
      }

      if (!confirm('Tem certeza que deseja deletar esta mensagem?')) {
        return
      }

      try {
        state.loading = true
        state.error = null

        const response = await mensagensRepository.MensagensDelete(state.mensagemAtual.id)

        if (response.data.success) {
          state.success = 'Mensagem deletada com sucesso!'
          resetarFormulario(state.tipoSelecionado)
          await buscarMensagens()
        }
      } catch (error) {
        console.error('Erro ao deletar:', error)
        state.error = 'Erro ao deletar mensagem'
      } finally {
        state.loading = false
      }
    }

    onMounted(() => {
      buscarMensagens()
    })

    return {
      state,
      tiposMensagem,
      selecionarTipo,
      handleFileUpload,
      removerImagem,
      salvar,
      deletar
    }
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h5>📧 Mensagens Automáticas</h5>
            <p class="text-sm mb-0">Configure mensagens que serão enviadas automaticamente por email e WhatsApp</p>
          </div>

          <div class="card-body">
            <!-- Alertas -->
            <div v-if="state.error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ state.error }}
              <button type="button" class="btn-close" @click="state.error = null"></button>
            </div>

            <div v-if="state.success" class="alert alert-success alert-dismissible fade show" role="alert">
              {{ state.success }}
              <button type="button" class="btn-close" @click="state.success = null"></button>
            </div>

            <!-- Tabs de Tipo de Mensagem -->
            <ul class="nav nav-pills mb-4" role="tablist">
              <li class="nav-item" v-for="tipo in tiposMensagem" :key="tipo.value">
                <button
                  class="nav-link"
                  :class="{ active: state.tipoSelecionado === tipo.value }"
                  @click="selecionarTipo(tipo.value)"
                  type="button"
                >
                  {{ tipo.label }}
                </button>
              </li>
            </ul>

            <!-- Descrição do Tipo -->
            <div class="alert alert-info mb-4">
              <i class="fas fa-info-circle me-2"></i>
              {{ tiposMensagem.find(t => t.value === state.tipoSelecionado)?.descricao }}
            </div>

            <!-- Formulário -->
            <div class="row">
              <!-- Título -->
              <div class="col-12 mb-3">
                <label class="form-label">Título da Mensagem</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="state.mensagemAtual.message_title"
                  placeholder="Ex: Feliz Aniversário!"
                />
              </div>

              <!-- Texto -->
              <div class="col-12 mb-3">
                <label class="form-label">Mensagem</label>
                <Editor
                  v-model="state.mensagemAtual.message_text"
                  api-key="c3pbj06iw6wid49ydqdu2wd42crd1p6f19ykut5pot19cf46"
                  :init="{
                    height: 300,
                    menubar: false,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                  }"
                />
              </div>

              <!-- Upload de Imagem -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Imagem (Opcional)</label>
                <div v-if="state.previewImagem" class="mb-2">
                  <img :src="state.previewImagem" alt="Preview" class="img-fluid rounded" style="max-height: 200px;">
                  <button type="button" class="btn btn-sm btn-danger mt-2" @click="removerImagem">
                    <i class="fas fa-trash me-1"></i> Remover Imagem
                  </button>
                </div>
                <input
                  type="file"
                  class="form-control"
                  accept="image/*"
                  @change="handleFileUpload"
                  :disabled="state.uploading"
                />
                <small class="text-muted">Formatos: JPG, PNG, GIF, WEBP (Máx: 5MB)</small>
                <div v-if="state.uploading" class="mt-2">
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  <span>Enviando...</span>
                </div>
              </div>

              <!-- Opções -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Opções</label>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="state.mensagemAtual.incluir_assinatura_treinador"
                    id="checkAssinatura"
                  />
                  <label class="form-check-label" for="checkAssinatura">
                    Incluir minha assinatura no final
                  </label>
                </div>
                <div class="form-check mt-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="state.mensagemAtual.ativo"
                    id="checkAtivo"
                  />
                  <label class="form-check-label" for="checkAtivo">
                    <strong>Mensagem ativa</strong> (será enviada automaticamente)
                  </label>
                </div>
              </div>

              <!-- Botões -->
              <div class="col-12">
                <button
                  type="button"
                  class="btn btn-success me-2"
                  @click="salvar"
                  :disabled="state.loading"
                >
                  <i class="fas fa-save me-1"></i>
                  {{ state.loading ? 'Salvando...' : 'Salvar' }}
                </button>
                <button
                  v-if="state.mensagemAtual.id"
                  type="button"
                  class="btn btn-danger"
                  @click="deletar"
                  :disabled="state.loading"
                >
                  <i class="fas fa-trash me-1"></i>
                  Deletar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nav-pills .nav-link {
  cursor: pointer;
}

.nav-pills .nav-link.active {
  background-color: #5e72e4;
}
</style>
